import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation as useRqMutation, useQuery } from "@tanstack/react-query";
import { useAction, useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  Clock,
  EllipsisVertical,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import { useGroupLabel } from "@/hooks/use-group-label";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import { extractConvexErrorMessage } from "@/lib/utils";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/admin/roles")({
  component: RolesSettings,
});

function RolesSettings() {
  const groupLabel = useGroupLabel();
  const { data: user } = useCurrentUser();
  const { data: companyUsers } = useQuery(convexQuery(api.users.getCompanyUsers, {}));
  const { data: invitations } = useQuery(convexQuery(api.invitations.getCompanyInvitations, {}));
  const { data: groupAdmins } = useQuery(convexQuery(api.groupMembers.getCompanyGroupAdmins, {}));

  const updateUserRole = useMutation(api.users.updateUserRole);
  const updateUserSponsor = useMutation(api.users.updateUserSponsor);
  const deleteCompanyUser = useAction(api.users.deleteCompanyUser);

  const [searchQuery, setSearchQuery] = useState("");
  const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{
    _id: string;
    name: string;
    email: string;
    imageUrl?: string;
  } | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"current" | "invited">("current");

  const createInvitation = useRqMutation({
    mutationFn: useConvexMutation(api.invitations.createInvitation),
  });

  const resetInviteForm = useCallback(() => {
    setInviteEmail("");
  }, []);

  const handleInviteDialogClose = useCallback(() => {
    setInviteOpen(false);
    resetInviteForm();
  }, [resetInviteForm]);

  const inviteIsDirty = inviteEmail.trim() !== "";

  const {
    showConfirmation: showInviteDiscard,
    guardedOnOpenChange: guardedInviteOnOpenChange,
    guardedClose: guardedInviteClose,
    confirmDiscard: confirmInviteDiscard,
    cancelDiscard: cancelInviteDiscard,
  } = useUnsavedChangesGuard({
    isDirty: inviteIsDirty,
    onClose: handleInviteDialogClose,
    setOpen: setInviteOpen,
  });

  const { mutate: revokeInvitation } = useRqMutation({
    mutationFn: useConvexMutation(api.invitations.revokeInvitation),
    onSuccess: () => toast.success("Invitation revoked"),
    onError: (error: Error) =>
      toast.error("Failed to revoke invitation", {
        description: extractConvexErrorMessage(error) || "Please try again",
      }),
  });

  const { mutate: resendInvitation } = useRqMutation({
    mutationFn: useConvexMutation(api.invitations.resendInvitation),
    onSuccess: () => toast.success("Invitation resent"),
    onError: (error: Error) =>
      toast.error("Failed to resend invitation", {
        description: extractConvexErrorMessage(error) || "Please try again",
      }),
  });

  const pendingInvitations = invitations?.filter((inv) => inv.status === "pending") ?? [];
  const groupAdminMap = new Map((groupAdmins ?? []).map((admin) => [admin._id, admin.groups]));

  const handleRoleChange = async (
    userId: any,
    newRole: "company_admin" | "rlg_admin" | "employee",
  ) => {
    try {
      await updateUserRole({ userId, role: newRole });
      const successMessage =
        newRole === "company_admin"
          ? "User promoted to organization admin"
          : newRole === "rlg_admin"
            ? "User promoted to Admin"
            : "User demoted from Admin";
      toast.success(successMessage);
    } catch (error) {
      toast.error("Failed to update role", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeletingUser(true);
    try {
      await deleteCompanyUser({ userId: userToDelete._id as any });
      toast.success(`${userToDelete.name} has been removed from the organization`);
      setDeleteUserDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      toast.error("Failed to remove user", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleSponsorChange = async (userId: any, isSponsor: boolean) => {
    try {
      await updateUserSponsor({ userId, isSponsor });
      toast.success(isSponsor ? "User marked as Sponsor" : "Sponsor label removed");
    } catch (error) {
      toast.error("Failed to update sponsor label", {
        description: error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      await createInvitation.mutateAsync({ email: inviteEmail.trim(), role: "employee" });
      toast.success("Invitation sent", {
        description: `An invitation has been sent to ${inviteEmail}`,
      });
      resetInviteForm();
      setInviteOpen(false);
    } catch (error) {
      const msg = extractConvexErrorMessage(error);
      const isAlreadySent =
        msg.includes("already been sent") || msg.includes("already has a pending");
      toast.error("Failed to send invitation", {
        description: isAlreadySent
          ? "The invitation has already been sent to this address and is now pending user response."
          : msg || "Please try again",
      });
    } finally {
      setIsInviting(false);
    }
  };

  const filteredMembers = companyUsers?.filter((u) => {
    if (u._id === user?._id) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const fullName = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      return fullName.includes(q) || u.email.toLowerCase().includes(q);
    }
    return true;
  });

  const totalCount = companyUsers?.filter((u) => u._id !== user?._id)?.length ?? 0;

  const formatTimeRemaining = (expiresAt: number) => {
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return "Expired";
    const days = Math.floor(remaining / 86400000);
    if (days > 0) return `${days}d left`;
    const hours = Math.floor(remaining / 3600000);
    if (hours > 0) return `${hours}h left`;
    return "<1h left";
  };

  const getRoleLabel = (role: string) => {
    if (role === "rlg_admin") return "Lead (Community admin)";
    return "Employee";
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                People ({totalCount})
              </CardTitle>
              <CardDescription>Manage people, roles and invitations.</CardDescription>
            </div>
            <Dialog open={inviteOpen} onOpenChange={guardedInviteOnOpenChange}>
              <DialogTrigger render={<Button size="sm" />}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Invite to organization</DialogTitle>
                  <DialogDescription>
                    Send an invitation to join the organization. After someone joins, you can make
                    them a {groupLabel.singularLower} admin from the members list of that group.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      disabled={isInviting}
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={guardedInviteClose}
                      disabled={isInviting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isInviting || !inviteEmail.trim()}>
                      {isInviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {isInviting ? "Sending..." : "Send invite"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "current" | "invited")}>
            <TabsList className="mb-4">
              <TabsTrigger value="current">Current ({filteredMembers?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="invited">Invited ({pendingInvitations.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="current" className="mt-0">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="space-y-1">
                {filteredMembers && filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => {
                    const name =
                      member.firstName && member.lastName
                        ? `${member.firstName} ${member.lastName}`
                        : member.email;
                    const initials =
                      member.firstName && member.lastName
                        ? `${member.firstName[0]}${member.lastName[0]}`
                        : member.email[0]?.toUpperCase();

                    return (
                      <div
                        key={member._id}
                        className="flex items-center justify-between rounded-lg p-3 transition-colors hover:bg-accent"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <Avatar className="shrink-0">
                            {member.imageUrl && <AvatarImage src={member.imageUrl} alt={name} />}
                            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{name}</p>
                            <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                            {member.role === "rlg_admin" && (
                              <p className="truncate text-xs text-muted-foreground">
                                Lead in{" "}
                                {(groupAdminMap.get(member._id)?.length ?? 0) === 1
                                  ? groupLabel.singularLower
                                  : groupLabel.pluralLower}
                                :{" "}
                                {groupAdminMap.get(member._id)?.length
                                  ? groupAdminMap
                                      .get(member._id)!
                                      .map((group) => group.groupName)
                                      .join(", ")
                                  : "-"}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {member.role === "company_admin" && (
                            <Badge variant="secondary">Admin</Badge>
                          )}
                          {member.role === "rlg_admin" && (
                            <Badge variant="secondary" className="gap-1">
                              <Shield className="h-3 w-3" />
                              {getRoleLabel(member.role)}
                            </Badge>
                          )}
                          {member.isSponsor && <Badge variant="secondary">Sponsor</Badge>}
                          <DropdownMenu>
                            <DropdownMenuTrigger className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                              <EllipsisVertical className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-44">
                              {member.role !== "company_admin" ? (
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(member._id, "company_admin")}
                                >
                                  Promote to Admin
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => handleRoleChange(member._id, "employee")}
                                >
                                  Demote from Admin
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleSponsorChange(member._id, !member.isSponsor)}
                              >
                                {member.isSponsor ? "Remove Sponsor" : "Assign Sponsor"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                variant="destructive"
                                onClick={() => {
                                  setUserToDelete({
                                    _id: member._id,
                                    name,
                                    email: member.email,
                                    imageUrl: member.imageUrl ?? undefined,
                                  });
                                  setDeleteUserDialogOpen(true);
                                }}
                              >
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No members found.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="invited" className="mt-0">
              {pendingInvitations.length > 0 ? (
                <div className="space-y-3">
                  {pendingInvitations.map((inv) => (
                    <div
                      key={inv._id}
                      className="flex items-center justify-between rounded-lg p-3 transition-colors hover:bg-accent"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <Avatar className="shrink-0">
                          <AvatarFallback>
                            <Mail className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{inv.email}</p>
                          <p className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatTimeRemaining(inv.expiresAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                            <EllipsisVertical className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => resendInvitation({ invitationId: inv._id })}
                            >
                              <RefreshCw className="h-4 w-4" />
                              Resend
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => revokeInvitation({ invitationId: inv._id })}
                            >
                              <Trash2 className="h-4 w-4" />
                              Revoke
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No pending invitations.
                </p>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={deleteUserDialogOpen} onOpenChange={setDeleteUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Remove User
            </DialogTitle>
          </DialogHeader>

          {userToDelete && (
            <div className="flex min-w-0 items-center gap-3 rounded-lg border bg-muted/50 p-3">
              <Avatar className="h-10 w-10 shrink-0">
                {userToDelete.imageUrl && (
                  <AvatarImage src={userToDelete.imageUrl} alt={userToDelete.name} />
                )}
                <AvatarFallback>
                  {userToDelete.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{userToDelete.name}</p>
                <p className="truncate text-xs text-muted-foreground">{userToDelete.email}</p>
              </div>
            </div>
          )}

          <DialogDescription>
            This will permanently remove this user from your organization. They will lose access to
            all groups, posts, and events. This action cannot be undone.
          </DialogDescription>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteUserDialogOpen(false)}
              disabled={isDeletingUser}
            >
              Cancel
            </Button>
            <Button variant="destructive" disabled={isDeletingUser} onClick={handleDeleteUser}>
              {isDeletingUser ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                "Remove User"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showInviteDiscard}
        onConfirm={confirmInviteDiscard}
        onCancel={cancelInviteDiscard}
      />
    </>
  );
}
